import { AdvertisementApi } from '@mbrtargeting/metatag-shared-types/ad-engine';
import { AdhesionUnitWatcherInterface, AdSlotDataValues, AdSlotIdDataInput, AdvertisingApi, AdvertisingSlotApi, ContentObjectInformation, ContentObjectWatcherInterface, ExtendedFeatureSettings, ExtendedFormatSettings, FormatMasterInterface, VisibilityNodeInformation, WindowEventDetailMap } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { addCssToElement, BrowserDetailsInterface, getBoundingClientRect, getBrowserDetails, isNotNull, Observable } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectFeatureSettings } from '../../../loader/essentials/config-selector.js';
import { triggerEvent } from '../../../loader/essentials/events.js';
import { MetaTag } from '../../../loader/essentials/init-metatag.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ADSLOT_REGISTRY, CONFIG_RESOLVER, METATAG } from '../../../loader/token.js';
import { WeakMap2 } from '../../../utils/weak-map2.js';
import { SlotEffects } from '../../effects/index.js';
import { ADHESION_UNIT_WATCHER, CONTENT_OBJECT_WATCHER, FEATURE_CONTROLLER, FORMAT_CONTROLLER, GLOBAL_SETTINGS, STORAGE_HELPER } from '../../token.js';
import { setIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { FeatureController } from '../feature-controller/feature-controller.js';
import { FormatController } from '../format-controller/format-controller.js';
import { GlobalSettings } from '../global-settings/global-settings.js';
import { StorageConsent, StorageHelper, StorageType } from '../storage/storage-helper.js';

const EmptyEffect = () => () => { };

@injectionTarget()
export class PublicAdvertisingApi implements AdvertisingApi {

    @inject(METATAG) #metaTag!: MetaTag;
    @inject(ADSLOT_REGISTRY) #adslotRegistry!: AdslotRegistry;
    @inject(STORAGE_HELPER) #storageHelper!: StorageHelper;
    @inject(CONTENT_OBJECT_WATCHER) #contentObjectWatcher!: ContentObjectWatcherInterface;
    @inject(ADHESION_UNIT_WATCHER) #adhesionUnitWatcher!: AdhesionUnitWatcherInterface;
    @inject(GLOBAL_SETTINGS) #globalSettings!: GlobalSettings;
    @inject(FORMAT_CONTROLLER) #formatController!: FormatController;
    @inject(CONFIG_RESOLVER) #configResolver!: ConfigResolver;
    @inject(FEATURE_CONTROLLER) #featureController!: FeatureController;

    #advertisingSlotFacades = new WeakMap2<Adslot, AdvertisingSlotApi>;

    constructor() {
        this.#metaTag.Advertising = this;
    }

    public triggerEvent<K extends keyof WindowEventDetailMap>(eventName: K, passedObject: WindowEventDetailMap[K]) {
        triggerEvent(eventName, passedObject);
    }

    public insertStyleToElement(cssText: string, node: HTMLElement): HTMLStyleElement {
        return addCssToElement(cssText, node);
    };

    public getBrowserDetails(): BrowserDetailsInterface {
        return getBrowserDetails();
    };

    public setLocalStorageData(key: string, value: string) {
        return this.#storageHelper.set(StorageType.LOCAL, StorageConsent.WAIT, key, value);
    };

    public getLocalStorageData(key: string): Promise<string | null> {
        return this.#storageHelper.get(StorageType.LOCAL, StorageConsent.WAIT, key);
    };

    public setSessionStorageData(key: string, value: string) {
        return this.#storageHelper.set(StorageType.SESSION, StorageConsent.WAIT, key, value);
    };

    public getSessionStorageData(key: string): Promise<string | null> {
        return this.#storageHelper.get(StorageType.SESSION, StorageConsent.WAIT, key);
    };

    public getBoundingClientRect(element: HTMLElement): DOMRect {
        return getBoundingClientRect(element);
    };

    public getContentObject(): ContentObjectInformation {
        return this.#contentObjectWatcher.contentObject;
    };

    public getContentObjectObservable(): Observable<ContentObjectInformation> {
        return this.#contentObjectWatcher.contentObject$;
    }

    public getAdhesionUnitWatcher(): AdhesionUnitWatcherInterface {
        return this.#adhesionUnitWatcher;
    };

    public changeAdhesionUnit(selector: string) {
        return this.#adhesionUnitWatcher.changeAdhesionUnit(selector);
    };

    public getStickyStop(): HTMLElement | null {
        const selector = this.#configResolver.get(selectFeatureSettings()).stickyStopUnitQuery || '';
        return (selector !== '') ? document.querySelector(selector) : null;
    }

    /**
     * @inheritDoc
     * executeLocalBackgroundColor & executeLocalBackgroundClickable defaults are overwritten when already present in getFeatureSettings()
     */
    public getWebsiteFeatures(): ExtendedFeatureSettings {
        return {
            executeLocalBackgroundColor: EmptyEffect,
            executeLocalBackgroundClickable: EmptyEffect,
            ...this.#configResolver.get(selectFeatureSettings()),
        };
    }

    /**
     * @inheritDoc
     * @interface
     */
    public getSlotByName(slotName: string): AdvertisingSlotApi | null {
        const slot = this.#adslotRegistry.getAdslot(slotName) as Adslot;
        if (isNotNull(slot)) {
            return this.#advertisingSlotFacades.getOrInsertComputed(slot, createAdvertisingSlotFacade) ?? null;
        }
        return null;
    };

    public getZone(): string {
        return this.#globalSettings.getZone();
    };

    public getPageType(): string {
        return this.#globalSettings.getPageType();
    };

    public getFormat(formatName: string): FormatMasterInterface | undefined {
        return this.#formatController.getFormat(formatName);
    }

    public editFormat(formatName: string, newFormatSettings: Partial<ExtendedFormatSettings>): void {
        return this.#formatController.editFormat(formatName, newFormatSettings);
    }

    public editFeatures(newFeatureSettings: Partial<ExtendedFeatureSettings>): void {
        return this.#featureController.editFeatures(newFeatureSettings);
    }
}

const createAdvertisingSlotFacade = (slot: Adslot): AdvertisingSlotApi => ({
    get slotName() { return slot.slotName },
    get containerNode() { return slot.containerNode },
    get adserverAnchor() { return slot.adserverAnchor },
    get adserverContainer() { return slot.adserverContainer },
    setIdsForAdSlot: (data: AdSlotIdDataInput) => setIdsForAdSlot(slot.slotName, data),
    reportDefaultDelivery: () => slot.reportDefaultDelivery(),
    reportBlockerDelivery: (shouldSlotContainerVanish?: boolean | undefined) => slot.reportBlockerDelivery(shouldSlotContainerVanish),
    reportAdDelivery: (width: number | string, height: number | string, priorityRequest: boolean = false) => {
        slot.reportAdDelivery(width, height, priorityRequest);
    },
    toggleAdLabel: (state: boolean) => slot.toggleEffect(SlotEffects.HIDE_AD_LABEL, !state),
    getPositionSettings: () => slot.getPositionSettings(),
    addEventListener: (eventName, eventCallback) => {
        slot.containerNode.addEventListener(eventName, eventCallback);
    },
    setAd: (advertisement: AdvertisementApi) => slot.setAd(advertisement),
    getFilteredKeyValues: () => slot.getFilteredKeyValues(),
    getAdUnitLevels: () => slot.getAdUnits(),
    /**
     * legacy part, fixes some templates
     */
    getContainer: () => slot.containerNode,
    getAdServerAnchor: () => slot.adserverAnchor,
    getAdServerContainer: () => slot.adserverContainer,
    getAdServerNode: () => slot.getAdServerNode(),
    setIdsForSystem: (systemName: string, dataValues: AdSlotDataValues) => {
        slot.setIdsForSystem(systemName, dataValues);
    },
    setFlag: (flagName: string, flagState: boolean) => {
        slot.setFlag(flagName, flagState);
    },
    getStatus: (statusName: string) => slot.getStatus(statusName),
    renderCreative: (renderInstructions) => slot.renderCreative(renderInstructions),
    addVisibilityNode: (nodeInformation: VisibilityNodeInformation) => slot.addVisibilityNode(nodeInformation),
    overwriteVisibilityNodes: (nodeInfoArray: VisibilityNodeInformation[]) => slot.overwriteVisibilityNodes(nodeInfoArray),
    getVisibilityNodes: () => slot.getVisibilityNodes(),
    getAdLabel: () => slot.getAdLabel()
});
